import {
  GetAccessDetailsRequest,
  GetAccessDetailsResponse,
  zGetAccessDetailsResponse,
} from '@upright/common/iam/access/access-details/api-types';
import { appendSearchParamsToUrlPath } from '@upright/common/utils/url-management';
import {
  AddOrEditAccessesRequest,
  ChangeAccessSpaceRequest,
  DeleteAccessesRequest,
  GetAccessListForObjectRequest,
  GetAccessListForObjectResponse,
  GetAccessListForSubjecRequestParams,
  GetAccessListForSubjectResponse,
  GetAccessParams,
  GetAccessResponse,
  GetMissingAccessesOfSubjectOnObjectRequest,
  GetMissingAccessesOfSubjectOnObjectResponse,
} from '../../../../common/iam/access/api-types';
import {
  AccessSubjectSearchRequest,
  AccessSubjectSearchResultsResponse,
} from '../../../../common/iam/access/search/types';
import {
  AccessObjectId,
  AccessObjectType,
  AccessSubject,
  AccessSubjectId,
  AccessSubjectType,
} from '../../../../common/iam/access/types';
import { fetchAndValidateOrThrow, fetchOrThrow, fetchOrThrowNoReturnData } from '../../common/fetchers/fetch-or-throw';
import { getRenderViewAccessToken } from '../../common/render-view-access-token';
import { optionsToString } from '../../common/util';
import { EntityTypeForAccessDetails } from '@upright/common/iam/access/access-details/types';

export async function fetchAccess(
  subjectId: AccessSubjectId,
  subjectType: AccessSubjectType,
  objectId: AccessObjectId,
  objectType: AccessObjectType,
) {
  const params: GetAccessParams = {
    subjectId,
    subjectType,
    objectId,
    objectType,
  };
  return await fetchOrThrow<GetAccessResponse>(`/access${optionsToString(params)}`, 'GET');
}

export async function fetchAccessListForSubject(
  subject: AccessSubject,
  accessObjectTypesToFetch: readonly AccessObjectType[],
) {
  const request: GetAccessListForSubjecRequestParams = {
    subjectId: subject.subjectId,
    subjectType: subject.subjectType,
    objectTypes: accessObjectTypesToFetch,
  };
  return await fetchOrThrow<GetAccessListForSubjectResponse>(`/access/subject/get${optionsToString(request)}`, 'GET');
}

export async function fetchMissingAccessesOfSubjectOnObject(
  subjectId: AccessSubjectId,
  subjectType: AccessSubjectType,
  objectId: AccessObjectId,
  objectType: AccessObjectType.COMPANY_GROUP | AccessObjectType.PORTFOLIO,
) {
  const request: GetMissingAccessesOfSubjectOnObjectRequest = {
    subjectId,
    subjectType,
    objectId,
    objectType,
  };
  return await fetchOrThrow<GetMissingAccessesOfSubjectOnObjectResponse>(
    '/access/subject/missing-accesses',
    'POST',
    request,
  );
}

export async function fetchAccessListForObject(objectType: AccessObjectType, objectId: AccessObjectId) {
  const request: GetAccessListForObjectRequest = {
    objectType,
    objectId,
  };
  return await fetchOrThrow<GetAccessListForObjectResponse>('/access/object/get', 'POST', request);
}

export async function addOrEditAccesses(request: AddOrEditAccessesRequest) {
  return await fetchOrThrowNoReturnData('/access', 'POST', request);
}

export async function deleteAccess(payload: DeleteAccessesRequest) {
  return await fetchOrThrowNoReturnData('/access', 'DELETE', payload);
}

export async function fetchAccessSubjectSearchResults(request: AccessSubjectSearchRequest) {
  const query = encodeURIComponent(request.query);
  const payload = { ...request, query };
  return await fetchOrThrow<AccessSubjectSearchResultsResponse>(`/access/search?payload=${JSON.stringify(payload)}`);
}

export async function patchAccessSpace(request: ChangeAccessSpaceRequest) {
  return await fetchOrThrowNoReturnData('/access/space', 'PATCH', request);
}

/**
 * Access details live behind two routes split by viewer auth state
 * (PLT-5177): `/access-details` (anonymous; publicly cacheable) and
 * `/access-details/authenticated` (auth required; never cached). The split
 * exists because the browser HTTP cache keys by URL only — auth rides in an
 * HttpOnly cookie the cache cannot vary on — so sharing one URL between
 * anonymous and logged-in viewers let a cached anonymous response be served
 * to a logged-in viewer (bogus paywalls, PLT-5124 redirect↔login loop).
 *
 * `viewerBelievesAuthenticated` is the client's own auth state (Redux
 * `loggedInUser`). It may be stale or still resolving; both directions are
 * safe:
 * - believes logged-in but is not → the authenticated route 401s and the
 *   fetcher runs its standard refresh-and-retry; nothing is cached.
 * - believes anonymous but has a valid cookie → the optional-auth route
 *   answers as authenticated and the server skips caching for logged-in
 *   responses.
 *
 * The headless render view authenticates via a Bearer token instead of
 * Redux state, so it is routed to the authenticated endpoint explicitly.
 */
export async function fetchAccessDetails<T extends EntityTypeForAccessDetails>(
  request: GetAccessDetailsRequest<T>,
  viewerBelievesAuthenticated: boolean,
) {
  const authenticated = viewerBelievesAuthenticated || getRenderViewAccessToken() !== null;
  const urlBase = authenticated ? '/access-details/authenticated' : '/access-details';
  const url = appendSearchParamsToUrlPath(urlBase, request);
  return await fetchAndValidateOrThrow<GetAccessDetailsResponse<T>>({
    path: url,
    schema: zGetAccessDetailsResponse,
  });
}
